import type { Session } from "@supabase/supabase-js";

const RESTORE_INTENT_KEY = "fintheon:desktop-auth-restore-intent";
const RESTORE_PATH = "/auth/desktop/restore";
const CALLBACK_PATH = "/auth/desktop/callback";
const RESTORE_INTENT_TTL_MS = 10 * 60 * 1000;

interface DesktopAuthRestoreIntent {
  ts: number;
  source?: string;
}

export function isDesktopAuthBridgePath(pathname?: string): boolean {
  const path =
    pathname ?? (typeof window === "undefined" ? "" : window.location.pathname);
  return path === RESTORE_PATH || path === CALLBACK_PATH;
}

export function markDesktopAuthRestoreIntent(source = "desktop"): void {
  try {
    const intent: DesktopAuthRestoreIntent = { ts: Date.now(), source };
    window.localStorage.setItem(RESTORE_INTENT_KEY, JSON.stringify(intent));
  } catch {
    /* ignore */
  }
}

export function hasRecentDesktopAuthRestoreIntent(now = Date.now()): boolean {
  try {
    const raw = window.localStorage.getItem(RESTORE_INTENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<DesktopAuthRestoreIntent>;
    if (!parsed.ts || now - parsed.ts > RESTORE_INTENT_TTL_MS) {
      clearDesktopAuthRestoreIntent();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function clearDesktopAuthRestoreIntent(): void {
  try {
    window.localStorage.removeItem(RESTORE_INTENT_KEY);
  } catch {
    /* ignore */
  }
}

export function buildDesktopDeepLink(session: Session): string | null {
  if (!session.access_token || !session.refresh_token) return null;
  const deepLink = new URL("fintheon://auth/callback");
  deepLink.searchParams.set("access_token", session.access_token);
  deepLink.searchParams.set("refresh_token", session.refresh_token);
  if (session.expires_in) {
    deepLink.searchParams.set("expires_in", String(session.expires_in));
  }
  deepLink.searchParams.set("token_type", session.token_type || "bearer");
  return deepLink.toString();
}
